import { ChangeDetectionStrategy, Component, computed, effect, input, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { DinamikBilesen } from '@service/fikoyt-ui/decorators/dinamik-bilesen';
import { FikoYTKokBilesenComponent } from '@/src/service/kok-bilesen/kok-bilesen.component';
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';

type Durum = 'kritik' | 'uyari' | 'normal';
const DURUM_BILGI: Record<Durum, { etiket: string; renk: string; rgb: string }> = {
  kritik: { etiket: 'Kritik', renk: '#ef4444', rgb: '239,68,68' },
  uyari:  { etiket: 'Uyarı',  renk: '#f59e0b', rgb: '245,158,11' },
  normal: { etiket: 'Normal', renk: '#22c55e', rgb: '34,197,94' },
};

// ---------------------------------------------------------------------------
// Edit (paylaş kutusu): kütük-stok ile paylaşılmış kartları listeler,
// seçilenler ek.seciliKutukler dizisine yazılır.
// ---------------------------------------------------------------------------
@Component({
  selector: 'kutuk-stok-pano-edit',
  standalone: true,
  imports: [FormsModule, NzInputModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="kpe">
      <div class="kpe-baslik">🪵 Panoya eklenecek kütükleri seçin</div>
      <input nz-input nzSize="small" [ngModel]="_filtre()" (ngModelChange)="_filtre.set($event)" placeholder="🔍 Ara..."/>
      @if (_yukleniyor()) { <div class="kpe-bilgi">Yükleniyor...</div> }
      @else if (!_tumKutukler().length) { <div class="kpe-bilgi">Kütük stok kartı bulunamadı. Önce kütük-stok ile kart paylaşın.</div> }
      @else {
        <div class="kpe-liste">
          @for (k of _filtreliKutukler(); track k._id) {
            <label class="kpe-kalem" [class.kpe-secili]="_seciliMi(k._id)">
              <input type="checkbox" [checked]="_seciliMi(k._id)" (change)="_toggle(k._id)"/>
              <span class="kpe-ad">{{ k.isim || k.baslik || k._id }}</span>
              @if (k.kalanStok != null) { <span class="kpe-stok">{{ k.kalanStok }} {{ k.birim || 'adet' }}</span> }
            </label>
          }
        </div>
        <div class="kpe-bilgi">{{ _seciliSayi() }} kütük seçildi</div>
      }
    </div>
  `,
  styles: [`
    .kpe { display:flex; flex-direction:column; gap:8px; }
    .kpe-baslik { font-weight:600; font-size:12px; color:var(--py-metin,#333); }
    .kpe-bilgi { font-size:11px; color:var(--py-metin-ikincil,#888); }
    .kpe-liste { display:flex; flex-direction:column; gap:4px; max-height:240px; overflow:auto; }
    .kpe-kalem { display:flex; align-items:center; gap:8px; padding:5px 8px; border-radius:6px;
      border:1px solid var(--py-kenarlik,#e5e7eb); cursor:pointer; font-size:12px; }
    .kpe-kalem:hover { background:rgba(0,0,0,.03); }
    .kpe-secili { border-color:#22c55e; background:rgba(34,197,94,.07); }
    .kpe-ad { flex:1; color:var(--py-metin,#333); }
    .kpe-stok { font-weight:700; font-size:11px; color:var(--py-metin-ikincil,#666); }
  `],
})
export class KutukStokPanoEditComponent extends FikoYTKokBilesenComponent {
  parametreler = model<any>();
  constructor() {
    super();
    effect(() => { this._kutukleriCek(); }, { allowSignalWrites: true });
  }

  private _istek: FikoYTIstekKok | null = null;
  _tumKutukler = signal<any[]>([]);
  _yukleniyor = signal(false);
  _filtre = signal('');

  p = computed(() => this.parametreler()?.ek || {});

  /** Seçili kütükleri ek.seciliKutukler dizisinde tutar */
  _seciliKutukler = computed<string[]>(() => {
    const v = this.p()?.seciliKutukler;
    return Array.isArray(v) ? v : [];
  });
  _seciliSayi = computed(() => this._seciliKutukler().length);

  _filtreliKutukler = computed(() => {
    const f = (this._filtre() || '').toLocaleLowerCase('tr');
    if (!f) return this._tumKutukler();
    return this._tumKutukler().filter(k =>
      (k.isim || k.baslik || '').toLocaleLowerCase('tr').includes(f));
  });

  _seciliMi(id: string) { return this._seciliKutukler().includes(id); }

  _toggle(id: string) {
    const params = this.parametreler();
    if (!params?.ek) return;
    const mevcut = [...this._seciliKutukler()];
    const i = mevcut.indexOf(id);
    if (i >= 0) mevcut.splice(i, 1); else mevcut.push(id);
    params.ek.seciliKutukler = mevcut;
    this.parametreler.set({ ...params });
  }

  /** Kütük-stok seti ile paylaşılmış kartları canlı listede göster */
  private async _kutukleriCek() {
    this._yukleniyor.set(true);
    try {
      if (!this._istek) this._istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate' });
      this._istek.parametreler = { aggregate: [
        { $match: { turu: 'KART', silindi: { $ne: true } } },
        { $lookup: { from: 'hareketler', localField: 'veri.icerikler', foreignField: '_id', as: 'ic' } },
        { $unwind: { path: '$ic', preserveNullAndEmptyArrays: false } },
        { $match: { 'ic.ek._secilenSetId': 'kutuk-stok' } },
        { $project: { _id: 1, isim: '$ic.ek.isim', baslik: '$veri.kurdela.baslik', birim: '$ic.ek.birim' } },
        { $sort: { _id: -1 } },
      ] };
      const cevap: any = await this._istek.isteSync();
      this._tumKutukler.set(cevap?.cevap?.sonuc ?? []);
    } catch (e) {
      console.warn('[KutukStokPano] Kütük listesi alınamadı:', e);
    } finally {
      this._yukleniyor.set(false);
    }
  }
}


// ---------------------------------------------------------------------------
// Görüntü kartı: seçili kütüklerin stok durumunu grid (döşeme) olarak gösterir
// ---------------------------------------------------------------------------
@Component({
  selector: 'kutuk-stok-pano-kart',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="kpp">
      @if (!_kutukler().length) {
        <div class="kpp-bos">🪵 Henüz kütük seçilmedi</div>
      } @else {
        <!-- Filtre + görünüm anahtarı -->
        <div class="kpp-araclar">
          <div class="kpp-filtreler">
            <button class="kpp-fbtn" [class.kpp-faktif]="_filtre()==='tumu'" (click)="_filtre.set('tumu')">Tümü ({{ _sayilar().tumu }})</button>
            <button class="kpp-fbtn kpp-kritik" [class.kpp-faktif]="_filtre()==='kritik'" (click)="_filtre.set('kritik')">Kritikler ({{ _sayilar().kritik }})</button>
            <button class="kpp-fbtn kpp-bitmis" [class.kpp-faktif]="_filtre()==='bitmis'" (click)="_filtre.set('bitmis')">Bitenler ({{ _sayilar().bitmis }})</button>
          </div>
          <button class="kpp-gorunum" (click)="_tabloMi.set(!_tabloMi())" [title]="_tabloMi() ? 'Kart görünümü' : 'Tablo görünümü'">
            {{ _tabloMi() ? '🗂' : '📋' }}
          </button>
        </div>

        @if (_filtreli().length === 0) {
          <div class="kpp-bos">Bu filtrede kütük yok</div>
        } @else if (_tabloMi()) {
          <!-- Tablo görünümü -->
          <table class="kpp-table">
            <thead><tr><th>Kütük</th><th>Stok</th><th>Birim</th><th>Limitler</th><th>Durum</th></tr></thead>
            <tbody>
              @for (k of _filtreli(); track k._id) {
                <tr>
                  <td class="kpp-t-ad">{{ k.isim || k.baslik || 'İsimsiz' }}</td>
                  <td class="kpp-t-stok">{{ k._kalan }}</td>
                  <td>{{ k.birim || 'adet' }}</td>
                  <td class="kpp-t-limit">k: {{ k.kritikLimit ?? '—' }} · u: {{ k.uyariLimit ?? '—' }}</td>
                  <td><span class="kpp-rozet" [style.background]="'rgba('+k._rgb+',.15)'" [style.color]="k._renk">{{ k._etiket }}</span></td>
                </tr>
              }
            </tbody>
          </table>
        } @else {
          <!-- Kart (grid) görünümü -->
          <div class="kpp-grid">
            @for (k of _filtreli(); track k._id) {
              <div class="kpp-kutu" [style.--kc]="k._renk">
                <div class="kpp-ust">
                  <span class="kpp-ad">{{ k.isim || k.baslik || 'İsimsiz' }}</span>
                  <span class="kpp-rozet" [style.background]="'rgba('+k._rgb+',.15)'" [style.color]="k._renk">{{ k._etiket }}</span>
                </div>
                <div class="kpp-stok">{{ k._kalan }} <span class="kpp-birim">{{ k.birim || 'adet' }}</span></div>
                <div class="kpp-bar"><div class="kpp-bar-ic" [style.width.%]="k._yuzde" [style.background]="k._renk"></div></div>
              </div>
            }
          </div>
        }
      }
    </div>
  `,
  styles: [`
    .kpp { display:block; }
    .kpp-bos { padding:16px; text-align:center; font-size:12px; color:var(--py-metin-ikincil,#999);
      border:1px dashed var(--py-kenarlik,#e5e7eb); border-radius:8px; }
    .kpp-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(130px, 1fr)); gap:8px; }
    .kpp-araclar { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:6px; }
    .kpp-filtreler { display:flex; gap:4px; flex-wrap:wrap; }
    .kpp-fbtn { padding:3px 10px; font-size:11px; font-weight:600; cursor:pointer;
      border:1px solid var(--py-kenarlik,#e5e7eb); border-radius:999px;
      background:var(--py-arkaplan,#fff); color:var(--py-metin,#444); transition:background .15s; }
    .kpp-fbtn:hover { background:rgba(0,0,0,.04); }
    .kpp-fbtn.kpp-kritik.kpp-faktif { background:#ef4444; border-color:#ef4444; color:#fff; }
    .kpp-fbtn.kpp-bitmis.kpp-faktif { background:#f59e0b; border-color:#f59e0b; color:#fff; }
    .kpp-fbtn.kpp-faktif:not(.kpp-kritik):not(.kpp-bitmis) { background:#22c55e; border-color:#22c55e; color:#fff; }
    .kpp-gorunum { padding:3px 8px; font-size:13px; cursor:pointer; border-radius:6px;
      border:1px solid var(--py-kenarlik,#e5e7eb); background:var(--py-arkaplan,#fff); }
    .kpp-gorunum:hover { background:rgba(0,0,0,.04); }
    .kpp-table { width:100%; border-collapse:collapse; font-size:12px; }
    .kpp-table th { text-align:left; font-size:10px; font-weight:700; text-transform:uppercase;
      letter-spacing:.05em; color:var(--py-metin-ucuncul,#999); padding:4px 8px;
      border-bottom:1px solid var(--py-kenarlik,#e5e7eb); }
    .kpp-table td { padding:5px 8px; border-bottom:1px solid var(--py-kenarlik,#f0f0f0);
      color:var(--py-metin,#333); }
    .kpp-table tr:hover td { background:rgba(0,0,0,.02); }
    .kpp-t-ad { font-weight:700; }
    .kpp-t-stok { font-weight:800; font-family:monospace; font-size:13px; }
    .kpp-t-limit { font-size:11px; color:var(--py-metin-ikincil,#888); }
    .kpp-kutu { display:flex; flex-direction:column; gap:4px; padding:8px 10px;
      border:1px solid var(--py-kenarlik,#e5e7eb); border-left:3px solid var(--kc,#22c55e);
      border-radius:8px; background:var(--py-arkaplan,#fff); }
    .kpp-ust { display:flex; align-items:center; justify-content:space-between; gap:4px; }
    .kpp-ad { font-size:12px; font-weight:700; color:var(--py-metin,#333);
      overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .kpp-rozet { font-size:9px; font-weight:700; text-transform:uppercase; padding:1px 5px; border-radius:4px; white-space:nowrap; }
    .kpp-stok { font-size:18px; font-weight:800; color:var(--py-metin,#222); }
    .kpp-birim { font-size:10px; font-weight:500; color:var(--py-metin-ikincil,#888); }
    .kpp-bar { height:4px; border-radius:2px; background:rgba(0,0,0,.06); overflow:hidden; }
    .kpp-bar-ic { height:100%; border-radius:2px; transition:width .5s; }
  `],
})
export class KutukStokPanoKartComponent extends FikoYTKokBilesenComponent {
  parametreler = input<any>();
  constructor() {
    super();
    effect(() => {
      const ids = this._seciliIds();
      if (ids.length) this._stoklariCek(ids);
    });
  }

  private _istek: FikoYTIstekKok | null = null;
  private _stoklar = signal<any[]>([]);

  _seciliIds = computed<string[]>(() => {
    const v = this.parametreler()?.ek?.seciliKutukler;
    return Array.isArray(v) ? v : [];
  });

  _kutukler = computed<any[]>(() => this._stoklar());

  /** Filtre + görünüm durumu */
  _filtre = signal<'tumu' | 'kritik' | 'bitmis'>('tumu');
  _tabloMi = signal(false);

  _sayilar = computed(() => {
    const l = this._kutukler();
    return {
      tumu: l.length,
      kritik: l.filter(k => k._etiket === 'Kritik').length,
      bitmis: l.filter(k => k._kalan <= 0).length,
    };
  });

  _filtreli = computed<any[]>(() => {
    const f = this._filtre();
    const l = this._kutukler();
    if (f === 'kritik') return l.filter(k => k._etiket === 'Kritik');
    if (f === 'bitmis') return l.filter(k => k._kalan <= 0);
    return l;
  });


  /** Seçili her kütüğün canlı stok durumunu tek aggregate ile çeker */
  private async _stoklariCek(ids: string[]) {
    try {
      if (!this._istek) this._istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate' });
      this._istek.parametreler = { aggregate: [
        { $match: { _id: { $in: ids }, silindi: { $ne: true } } },
        { $lookup: { from: 'hareketler', let: { pid: '$_id' }, pipeline: [
          { $match: { $expr: { $eq: ['$_uid', '$$pid'] }, silindi: { $ne: true } } },
          { $lookup: { from: 'hareketler', localField: 'veri.icerikler', foreignField: '_id', as: 'ic' } },
          { $unwind: { path: '$ic', preserveNullAndEmptyArrays: false } },
          { $match: { 'ic.ek._secilenSetId': 'kutuk-stok.hareket' } },
          { $project: {
            tip: { $ifNull: ['$ic.ek.tip', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'tip'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', 'giris'] } } } ] },
            miktar: { $toDouble: { $ifNull: ['$ic.ek.miktar', { $let: { vars: { p: { $arrayElemAt: [ { $filter: { input: { $ifNull: ['$ic.ek.parametreler', []] }, as: 'x', cond: { $eq: ['$$x.ismi', 'miktar'] } } }, 0 ] } }, in: { $ifNull: ['$$p.degeri', 0] } } } ] } },
          } },
        ], as: '_h' } },
        { $addFields: {
          _kalan: { $round: [{ $reduce: {
            input: '$_h', initialValue: 0,
            in: { $switch: { branches: [
              { case: { $eq: ['$$this.tip', 'giris'] }, then: { $add: ['$$value', '$$this.miktar'] } },
              { case: { $in: ['$$this.tip', ['cikis', 'fire']] }, then: { $subtract: ['$$value', '$$this.miktar'] } },
              { case: { $eq: ['$$this.tip', 'sayim'] }, then: '$$value' },
            ], default: '$$value' } } },
          }, 0] },
        } },
        { $lookup: { from: 'hareketler', localField: 'veri.icerikler', foreignField: '_id', as: 'ic2' } },
        { $unwind: { path: '$ic2', preserveNullAndEmptyArrays: true } },
        { $match: { $or: [ { ic2: null }, { 'ic2.ek._secilenSetId': 'kutuk-stok' } ] } },
        { $project: {
          _id: 1, isim: '$ic2.ek.isim', baslik: '$veri.kurdela.baslik',
          birim: '$ic2.ek.birim', uyariLimit: '$ic2.ek.uyariLimit', kritikLimit: '$ic2.ek.kritikLimit',
          _kalan: 1,
        } },
      ] };
      const cevap: any = await this._istek.isteSync();
      const liste: any[] = (cevap?.cevap?.sonuc ?? []).map((k: any) => {
        const kalan = +(k._kalan ?? 0);
        const kritik = +(k.kritikLimit || 0);
        const uyari = +(k.uyariLimit || 0);
        const durum: Durum = (kritik && kalan <= kritik) ? 'kritik' : (uyari && kalan <= uyari) ? 'uyari' : 'normal';
        const b = DURUM_BILGI[durum];
        const tavan = uyari > 0 ? uyari * 1.6 : Math.max(kalan, 1);
        return {
          ...k,
          _kalan: kalan,
          _etiket: b.etiket, _renk: b.renk, _rgb: b.rgb,
          _yuzde: Math.max(4, Math.min(100, (kalan / tavan) * 100)),
        };
      });
      // En düşük stok başa gelecek şekilde sırala
      liste.sort((a, b) => a._kalan - b._kalan);
      this._stoklar.set(liste);
    } catch (e) {
      console.warn('[KutukStokPano] Stok bilgisi alınamadı:', e);
    }
  }
}

// ---------------------------------------------------------------------------
// kutuk-stok-pano DinamikBilesen kaydı
// ---------------------------------------------------------------------------
@DinamikBilesen({
  id: 'kutuk-stok-pano',
  baslik: 'Kütük Stok Panosu',
  aciklama: 'Seçilen kütüklerin stok durumunu grid olarak gösterir',
  ikon: 'grid',
  kategori: 'kutuk-stok',
})
class KutukStokPano {
  paylas_kutusu         = KutukStokPanoEditComponent;
  paylasilmis_kart      = KutukStokPanoKartComponent;
  paylasilmis_kart_sade = KutukStokPanoKartComponent;
}
